export const MAX_TITLE_LENGTH = 140;
export const MAX_QUESTION_LENGTH = 500;
export const MARKS_OUT_OF_MIN = 1;
export const MARKS_OUT_OF_MAX = 100;
export const DEFAULT_MARKS_OUT_OF = 20;

// Feedback type identifiers used in the question form
export const FEEDBACK_TYPE_IDS = {
  SELF_ASSESSMENT: 'self-assessment-areas',
  COMMENT_BANKS: 'comment-banks',
  SUCCESS_CRITERIA: 'success-criteria',
  MARKING_TEMPLATES: 'marking-templates',
  SHORT_ANSWERS: 'short-answers',
  COURSE_MATERIALS: 'course-materials',
};

export const FEEDBACK_TYPES_CONFIG = {
  [FEEDBACK_TYPE_IDS.SELF_ASSESSMENT]: {
    id: FEEDBACK_TYPE_IDS.SELF_ASSESSMENT,
    displayName: 'Self-assessment Areas',
    description: 'Focus areas that help students evaluate and reflect on their own work.',
    tags: ['student-self-evaluation', 'reflection', 'pre-feedback'],
  },
  [FEEDBACK_TYPE_IDS.COMMENT_BANKS]: {
    id: FEEDBACK_TYPE_IDS.COMMENT_BANKS,
    displayName: 'Specific Comments',
    description: 'Pre-written feedback comments that can be quickly inserted into student responses.',
    tags: ['inline-feedback', 'comment-insertion', 'general'],
  },
  [FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA]: {
    id: FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA,
    displayName: 'Success Criteria',
    description: 'Specific criteria that define what successful completion of the task looks like.',
    tags: ['criteria-based', 'checklist', 'structured'],
  },
  [FEEDBACK_TYPE_IDS.MARKING_TEMPLATES]: {
    id: FEEDBACK_TYPE_IDS.MARKING_TEMPLATES,
    displayName: 'Extended Response Rubrics',
    description: 'Scoring guides with criteria and levels to assess extended writing tasks.',
    tags: ['rubric', 'extended-response', 'scoring-guide'],
  },
  [FEEDBACK_TYPE_IDS.SHORT_ANSWERS]: {
    id: FEEDBACK_TYPE_IDS.SHORT_ANSWERS,
    displayName: 'Short Answer Marking Criteria',
    description: 'Marking guides with expected answers and point allocations for short response questions.',
    tags: ['short-answer', 'answer-key', 'marks-based'],
  },
  [FEEDBACK_TYPE_IDS.COURSE_MATERIALS]: {
    id: FEEDBACK_TYPE_IDS.COURSE_MATERIALS,
    displayName: 'Course Materials',
    description: 'Curriculum outcomes JeddAI references when marking and writing feedback.',
    tags: ['curriculum', 'outcomes', 'humanities'],
  },
};

const isShortAnswer = (v) => {
  const normalized = String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  // "Short Response" is AU-VIC-EnglishJnr's label for the same thing.
  return normalized.includes('shortanswer') || normalized.includes('shortresponse');
};
const isExtended = (v) => {
  const normalized = String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return normalized.includes('extended') || normalized.includes('longanswer');
};

/**
 * Mirrors the server's humanities rule (SubjectFamilies) closely enough to decide whether to
 * SHOW the Course Materials row. The server remains the authority — it re-checks on read and
 * again on save — but visibility cannot wait for a network round trip, and inferring it from
 * an empty response is wrong whenever the subject filter itself was empty.
 *
 * Longest keyword wins, matching the server, so "Political Science" is humanities rather than
 * STEM and "English Literature" stays English.
 */
const SUBJECT_FAMILY_KEYWORDS = [
  ['english', 'english'], ['literature', 'english'], ['writing', 'english'],
  ['math', 'stem'], ['physics', 'stem'], ['chemistry', 'stem'], ['biology', 'stem'],
  ['science', 'stem'], ['engineering', 'stem'], ['computing', 'stem'],
  ['softwaredesign', 'stem'], ['informationtechnology', 'stem'],
  ['history', 'humanities'], ['geography', 'humanities'], ['economics', 'humanities'],
  ['business', 'humanities'], ['commerce', 'humanities'], ['legalstudies', 'humanities'],
  ['civics', 'humanities'], ['government', 'humanities'], ['socialstudies', 'humanities'],
  ['society', 'humanities'], ['religion', 'humanities'], ['philosophy', 'humanities'],
  ['politics', 'humanities'], ['political', 'humanities'], ['classicalstudies', 'humanities'],
  ['classics', 'humanities'], ['sociology', 'humanities'], ['anthropology', 'humanities'],
  ['archaeology', 'humanities'], ['ancient', 'humanities'], ['humanities', 'humanities'],
  ['socialscience', 'humanities'],
];

const normalizeSubjectToken = (v) =>
  String(v || '').toLowerCase().replace(/[^a-z]/g, '');

export const isHumanitiesSubject = (subject) => {
  if (!subject) return false;
  const key =
    normalizeSubjectToken(subject.subjectCode || subject.code) +
    normalizeSubjectToken(subject.subjectName || subject.name || subject.title);
  if (!key) return false;
  let best = null;
  SUBJECT_FAMILY_KEYWORDS.forEach(([word, family]) => {
    if (!key.includes(word)) return;
    if (!best || word.length > best[0].length) best = [word, family];
  });
  return best !== null && best[1] === 'humanities';
};

export const isFeedbackTypeApplicable = () => true;

export const isFeedbackTypeVisibleForContext = (feedbackTypeId, { responseType, taskType } = {}) => {
  if (!FEEDBACK_TYPES_CONFIG[feedbackTypeId]) return false;
  if (feedbackTypeId === FEEDBACK_TYPE_IDS.COMMENT_BANKS && (isShortAnswer(responseType) || isShortAnswer(taskType))) return false;
  if (feedbackTypeId === FEEDBACK_TYPE_IDS.MARKING_TEMPLATES && (isShortAnswer(responseType) || isShortAnswer(taskType))) return false;
  // Course Materials is no longer a per-question feedback-type row. Teachers attach
  // content at task level (Create Task → Advanced → Add course materials) and the
  // backend auto-selects relevant syllabus outcomes at marking time; the id survives
  // only because stored feedbackTypes arrays may still carry it.
  if (feedbackTypeId === FEEDBACK_TYPE_IDS.COURSE_MATERIALS) return false;
  if (feedbackTypeId === FEEDBACK_TYPE_IDS.SHORT_ANSWERS) {
    const taskTypeIsShortAnswer = isShortAnswer(taskType);
    const responseTypeIsShortAnswer = isShortAnswer(responseType);
    if (!taskTypeIsShortAnswer && !responseTypeIsShortAnswer) {
      return false;
    }
  }
  return true;
};

export const getApplicableFeedbackTypes = (responseType) => {
  return Object.values(FEEDBACK_TYPES_CONFIG).filter((c) => {
    if (c.id === FEEDBACK_TYPE_IDS.COMMENT_BANKS && isShortAnswer(responseType)) return false;
    if (c.id === FEEDBACK_TYPE_IDS.MARKING_TEMPLATES && isShortAnswer(responseType)) return false;
    if (c.id === FEEDBACK_TYPE_IDS.SHORT_ANSWERS && isExtended(responseType)) return false;
    if (c.id === FEEDBACK_TYPE_IDS.COURSE_MATERIALS) return false;
    return true;
  });
};

export const getIncompatibleFeedbackTypes = (oldResponseType, newResponseType) => {
  const oldApplicable = new Set(
    getApplicableFeedbackTypes(oldResponseType).map((c) => c.id)
  );
  const newApplicable = new Set(
    getApplicableFeedbackTypes(newResponseType).map((c) => c.id)
  );
  return [...oldApplicable].filter((id) => !newApplicable.has(id));
};

export const FEEDBACK_METHODS = [
  {
    value: 'JEDDAI_ASSISTED',
    label: 'Teacher (AI-assisted)',
    tooltip:
      'JeddAI provides instant feedback suggestions that you can review and edit before submitting.',
  },
  {
    value: 'TEACHER',
    label: 'Teacher (Manual)',
    tooltip: 'Each submission will be reviewed by the teacher',
  },
  {
    value: 'JEDDAI',
    label: 'JeddAI (Instant)',
    tooltip:
      'JeddAI provides instant feedback to students without teacher input.',
  },
  {
    value: 'P2P',
    label: 'Peer to Peer',
    tooltip:
      "Students mark each other's work anonymously. Use the drag-and-drop feature to pair specific students together",
  },
];

// Response type configuration with tooltips and paragraph count settings
// Maps response type display names and codes to their metadata
export const RESPONSE_TYPES_CONFIG = {
  'Targeted Paragraphs': {
    description: 'Students write specific paragraph types (intro, body, conclusion) with structured feedback.',
    showParagraphCounts: true,
  },
  'TARGETED_PARAGRAPHS': {
    description: 'Students write specific paragraph types (intro, body, conclusion) with structured feedback.',
    showParagraphCounts: true,
  },
  'Extended Response': {
    description: 'Students write a full essay or long-form response to the task.',
    showParagraphCounts: false,
  },
  'LONG_ANSWER': {
    description: 'Students write a full essay or long-form response to the task.',
    showParagraphCounts: false,
  },
  'Long Answer': {
    description: 'Students write a full essay or long-form response to the task.',
    showParagraphCounts: false,
  },
  'Short Answer': {
    description: 'Students provide brief responses to specific questions or prompts.',
    showParagraphCounts: false,
  },
  'SHORT_ANSWER': {
    description: 'Students provide brief responses to specific questions or prompts.',
    showParagraphCounts: false,
  },
  'Short Answers': {
    description: 'Students provide brief responses to specific questions or prompts.',
    showParagraphCounts: false,
  },
  'SHORT_ANSWERS': {
    description: 'Students provide brief responses to specific questions or prompts.',
    showParagraphCounts: false,
  },
};

export const normalizeType = (str) => String(str || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export const isShortAnswerType = (type) => {
  const normalized = normalizeType(type);
  // "Short Response" is AU-VIC-EnglishJnr's label for the same thing.
  return normalized.includes('shortanswer') || normalized.includes('shortresponse');
};
export const isExtendedResponseType = (type) => {
  const normalized = normalizeType(type);
  return normalized.includes('extendedresponse') || normalized.includes('longanswer');
};
export const isTargetedParagraphsType = (type) => normalizeType(type).includes('targetedparagraph');

export const getResponseTypeConfig = (label) => {
  if (!label) return null;
  if (RESPONSE_TYPES_CONFIG[label]) return RESPONSE_TYPES_CONFIG[label];
  const normalizedLabel = normalizeType(label);
  for (const key of Object.keys(RESPONSE_TYPES_CONFIG)) {
    if (normalizeType(key) === normalizedLabel) return RESPONSE_TYPES_CONFIG[key];
  }
  if (isTargetedParagraphsType(label)) {
    return { showParagraphCounts: true, description: '' };
  }
  return null;
};
